// CareBot 开通弹窗（v2）
//
// 职责（相对 v1 重构）：
// - v1：被动接受 onboardResult，仅展示 QR/family
// - v2：主动驱动整套流程 — phone 收集 → SMS 验证 → onboard → claim/family
//
// 状态机：
//   ┌───────────────┐  existingPhone 非空        ┌───────┐
//   │  open=true    │ ────────────────────────▶ │ claim │
//   └───────┬───────┘                            └───────┘
//           │ existingPhone 为空                      ▲
//           ▼                                         │ onboard 成功
//   ┌───────────────┐  发送验证码 ┌──────┐  提交     │
//   │     phone     │ ──────────▶ │ sms  │ ─────────┘
//   └───────────────┘             └──────┘
//
// 设计原则：
// - 关闭弹窗保留态，避免顾问误关丢失 shortCode/code
// - SMS 验证码 60s 冷却倒计时；倒计时内禁用"重发"
// - onboard 由 Modal 内部触发，Button 仅决定开关与文案
// - 若已激活老人重开 Modal，existingPhone 通常已有值（onboard 成功时 Rails 回写过），
//   直接幂等 onboard 拿最新 claimUrl/expiresAt

import React, { useEffect, useRef, useState } from 'react';
import { Copy, Send, MessageCircle, RefreshCw, Heart } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { QRCodeDisplay } from '../common/QRCodeDisplay';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { useToastContext } from '../../contexts/ToastContext';
import { carebotOnboardingService } from '../../services/carebotOnboardingService';
import {
  CareBotFamilyInvite,
  CareBotOnboardResponse,
  CareBotOnboardingError,
} from '../../types/carebotOnboarding';

interface CareBotActivationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string | number;
  assessmentId: string | number;
  needReportId?: string | number;
  existingPhone: string;        // 来自 user.phone，可空
  elderName?: string;
  onOnboardSuccess?: () => void;
}

const PHONE_RE = /^1[3-9]\d{9}$/;
const SMS_CODE_LEN = 6;
const RESEND_COOLDOWN_SECONDS = 60;

type Step = 'phone' | 'sms' | 'claim' | 'family';

function formatShortCode(code: string): string {
  if (!code) return '';
  if (code.length <= 4) return code;
  const mid = Math.ceil(code.length / 2);
  return `${code.slice(0, mid)} ${code.slice(mid)}`;
}

export const CareBotActivationModal: React.FC<CareBotActivationModalProps> = ({
  open,
  onOpenChange,
  userId,
  assessmentId,
  needReportId,
  existingPhone,
  elderName,
  onOnboardSuccess,
}) => {
  const toast = useToastContext();
  const { copy } = useCopyToClipboard();

  const [step, setStep] = useState<Step>('phone');
  const [onboardResult, setOnboardResult] = useState<CareBotOnboardResponse | null>(null);

  // phone 收集相关
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [smsCodeInput, setSmsCodeInput] = useState('');
  const [smsCodeError, setSmsCodeError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [smsSendLoading, setSmsSendLoading] = useState(false);
  const [onboardLoading, setOnboardLoading] = useState(false);

  // family invite 相关（v1 沿用）
  const [familyPhone, setFamilyPhone] = useState('');
  const [familyPhoneError, setFamilyPhoneError] = useState<string | null>(null);
  const [familyInvite, setFamilyInvite] = useState<CareBotFamilyInvite | null>(null);
  const [familySmsSent, setFamilySmsSent] = useState(false);
  const [familyLoading, setFamilyLoading] = useState(false);

  // 防止 open 切换时重复 onboard
  const onboardedOnceRef = useRef(false);

  const isMock = carebotOnboardingService.isMockMode();
  const familyInviteEnabled = carebotOnboardingService.isFamilyInviteEnabled();

  // open=true 时根据 existingPhone 决定初始流程
  useEffect(() => {
    if (!open) {
      // 关闭时不重置 onboardResult / step，以便重开仍能看到 shortCode（保留态）
      onboardedOnceRef.current = onboardResult !== null;
      return;
    }

    if (onboardResult) {
      setStep('claim');
      return;
    }

    if (existingPhone && PHONE_RE.test(existingPhone)) {
      // user.phone 已存在 → 直接走 onboard（不传 elderPhone+smsCode）
      if (onboardedOnceRef.current) return;
      onboardedOnceRef.current = true;
      runOnboard({});
    } else {
      setStep('phone');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 倒计时
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const runOnboard = async (extra: { elderPhone?: string; smsCode?: string }) => {
    setOnboardLoading(true);
    try {
      const res = await carebotOnboardingService.onboard({
        userId,
        assessmentId,
        needReportId,
        ...extra,
      });
      setOnboardResult(res);
      setStep('claim');
      onOnboardSuccess?.();
    } catch (err) {
      const msg =
        err instanceof CareBotOnboardingError
          ? err.message
          : err instanceof Error
            ? err.message
            : '开通失败，请稍后重试';
      const code = err instanceof CareBotOnboardingError ? err.code : undefined;

      // SMS 错码场景：留在 sms 步骤让顾问重输
      if (code === 'invalid_sms_code') {
        setSmsCodeError(msg);
        setStep('sms');
      } else if (code === 'missing_elder_phone' || code === 'missing_elder_phone_or_sms_code') {
        // existingPhone 实际为空但前端误判 → 退回 phone 步骤
        setStep('phone');
        setPhoneError(msg);
      } else {
        toast.error('开通失败', msg);
        // 退回 phone 步骤（如有 phone 输入则保留）
        if (!onboardResult) setStep(existingPhone ? 'phone' : 'phone');
      }
      onboardedOnceRef.current = false;
    } finally {
      setOnboardLoading(false);
    }
  };

  const handleSendSms = async () => {
    setPhoneError(null);
    if (!PHONE_RE.test(phoneInput)) {
      setPhoneError('请输入有效的 11 位手机号');
      return;
    }
    setSmsSendLoading(true);
    try {
      await carebotOnboardingService.sendElderSms(phoneInput, userId);
      toast.success('验证码已发送', `请让老人将收到的 6 位验证码告诉你`);
      setStep('sms');
      setCountdown(RESEND_COOLDOWN_SECONDS);
      setSmsCodeInput('');
      setSmsCodeError(null);
    } catch (err) {
      const msg =
        err instanceof CareBotOnboardingError
          ? err.message
          : err instanceof Error
            ? err.message
            : '验证码发送失败，请稍后重试';
      setPhoneError(msg);
    } finally {
      setSmsSendLoading(false);
    }
  };

  const handleResendSms = () => {
    if (countdown > 0 || smsSendLoading) return;
    handleSendSms();
  };

  const handleSubmitOnboard = async () => {
    setSmsCodeError(null);
    if (smsCodeInput.length !== SMS_CODE_LEN) {
      setSmsCodeError(`请输入 ${SMS_CODE_LEN} 位验证码`);
      return;
    }
    await runOnboard({ elderPhone: phoneInput, smsCode: smsCodeInput });
  };

  const handleCopy = async (text: string, label: string) => {
    const ok = await copy(text);
    if (ok) toast.success(`${label}已复制`);
    else toast.error('复制失败', '请手动长按选择文本复制');
  };

  const handleSendFamily = async () => {
    setFamilyPhoneError(null);
    if (!PHONE_RE.test(familyPhone)) {
      setFamilyPhoneError('请输入有效的 11 位手机号');
      return;
    }
    if (!onboardResult) {
      // 防御：理论上 family 步骤需要 onboardResult 提供 elderPhone
      toast.error('请先完成开通', '当前评估尚未生成老人邀请码');
      return;
    }
    setFamilyLoading(true);
    try {
      const res = await carebotOnboardingService.sendFamilyInvite({
        assessmentId,
        elderPhone: onboardResult.elder ? phoneInput || existingPhone : existingPhone,
        familyPhone,
      });
      setFamilyInvite(res.familyInvite);
      setFamilySmsSent(res.smsSent);
      if (res.smsSent) {
        toast.success('短信已发送', `家属邀请已通过短信发送至 ${familyPhone}`);
      } else {
        toast.success('家属邀请已生成', '短信通道未启用，请把链接或二维码转给家属');
      }
    } catch (err) {
      const msg =
        err instanceof CareBotOnboardingError
          ? err.message
          : err instanceof Error
            ? err.message
            : '发送失败，请稍后重试';
      toast.error('发送失败', msg);
    } finally {
      setFamilyLoading(false);
    }
  };

  // 渲染：phone 收集步骤
  const renderPhoneStep = () => (
    <div className="space-y-4 pt-2">
      <div className="text-sm text-gray-600">
        该老人尚未在 ICOPE 留下手机号。请填写老人手机号（不能是顾问自己的号），
        发送验证码后由老人确认收到，再提交开通。
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">老人手机号</label>
        <Input
          type="tel"
          inputMode="numeric"
          maxLength={11}
          placeholder="请输入 11 位手机号"
          value={phoneInput}
          onChange={(e) => {
            setPhoneInput(e.target.value.replace(/\D/g, ''));
            if (phoneError) setPhoneError(null);
          }}
          disabled={smsSendLoading}
        />
        {phoneError && <p className="text-xs text-red-600">{phoneError}</p>}
      </div>
      <Button
        onClick={handleSendSms}
        disabled={smsSendLoading || phoneInput.length !== 11}
        className="w-full"
        size="lg"
      >
        {smsSendLoading ? (
          <>
            <RefreshCw className="w-4 h-4 animate-spin" />
            发送中...
          </>
        ) : (
          <>
            <Send className="w-4 h-4" />
            发送验证码
          </>
        )}
      </Button>
    </div>
  );

  // 渲染：SMS 验证步骤
  const renderSmsStep = () => (
    <div className="space-y-4 pt-2">
      <div className="text-sm text-gray-600">
        验证码已发送到 <span className="font-medium text-gray-800">{phoneInput}</span>。
        请让老人念出短信中的 6 位数字。
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">6 位验证码</label>
        <Input
          type="text"
          inputMode="numeric"
          maxLength={SMS_CODE_LEN}
          placeholder="请输入 6 位验证码"
          value={smsCodeInput}
          onChange={(e) => {
            setSmsCodeInput(e.target.value.replace(/\D/g, '').slice(0, SMS_CODE_LEN));
            if (smsCodeError) setSmsCodeError(null);
          }}
          disabled={onboardLoading}
        />
        {smsCodeError && <p className="text-xs text-red-600">{smsCodeError}</p>}
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          className="flex-1"
          disabled={countdown > 0 || smsSendLoading}
          onClick={handleResendSms}
        >
          {countdown > 0 ? `${countdown}s 后可重发` : '重发验证码'}
        </Button>
        <Button
          className="flex-1"
          disabled={onboardLoading || smsCodeInput.length !== SMS_CODE_LEN}
          onClick={handleSubmitOnboard}
        >
          {onboardLoading ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              提交中...
            </>
          ) : (
            <>
              <Heart className="w-4 h-4" />
              提交开通
            </>
          )}
        </Button>
      </div>
      <button
        type="button"
        className="text-xs text-gray-500 hover:text-gray-700 underline"
        onClick={() => {
          setStep('phone');
          setSmsCodeInput('');
          setSmsCodeError(null);
        }}
      >
        修改手机号
      </button>
    </div>
  );

  // 渲染：claim（onboard 成功后的二维码 + 邀请码）
  const renderClaimStep = () => {
    if (!onboardResult) {
      return (
        <div className="space-y-4 pt-4 text-center">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto text-emerald-500" />
          <div className="text-sm text-gray-600">正在开通 CareBot...</div>
        </div>
      );
    }
    const claim = onboardResult.elderClaim;
    const subscription = onboardResult.subscription;
    return (
      <div className="space-y-4 pt-2">
        <div className="flex flex-col items-center gap-3">
          <QRCodeDisplay value={claim.claimUrl} size={196} ariaLabel="老人认领二维码" />
          <p className="text-sm text-gray-500">使用微信扫码即可注册并认领</p>
        </div>

        <div className="bg-gray-50 rounded-xl p-4 flex flex-col items-center gap-2">
          <span className="text-xs text-gray-500">或在 CareBot 注册页输入邀请码</span>
          <span className="text-3xl font-bold tracking-widest text-gray-800 font-mono select-all">
            {formatShortCode(claim.shortCode)}
          </span>
          <span className="text-xs text-gray-400">
            有效期至 {new Date(claim.expiresAt).toLocaleString('zh-CN')}
          </span>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            size="default"
            className="flex-1"
            onClick={() => handleCopy(claim.shortCode, '邀请码')}
          >
            <Copy className="w-4 h-4" />
            复制邀请码
          </Button>
          <Button
            variant="outline"
            size="default"
            className="flex-1"
            onClick={() => handleCopy(claim.claimUrl, '认领链接')}
          >
            <Copy className="w-4 h-4" />
            复制链接
          </Button>
        </div>

        <div className="text-xs text-gray-500 bg-blue-50 border border-blue-100 rounded-lg p-3">
          <div>
            体验订阅状态：
            <span className="font-medium text-gray-700">
              {subscription.status === 'active' ? '已激活' : subscription.status}
            </span>
            ・到期：
            <span className="font-medium text-gray-700">
              {new Date(subscription.expiresAt).toLocaleDateString('zh-CN')}
            </span>
          </div>
          <div className="mt-1">
            老人注册成功后，可在 CareBot 内查看 ICOPE 评估结果，并在 CareBot 内邀请家属。
          </div>
        </div>
      </div>
    );
  };

  // 渲染：family invite（沿用 v1）
  const renderFamilyStep = () => (
    <div className="space-y-4 pt-2">
      {!familyInvite && (
        <>
          <div className="text-sm text-gray-600">
            填写家属手机号，生成家属邀请链接。家属点击链接后可在 CareBot 中查看老人 ICOPE 报告摘要并陪同注册。
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">家属手机号</label>
            <Input
              type="tel"
              inputMode="numeric"
              maxLength={11}
              placeholder="请输入 11 位手机号"
              value={familyPhone}
              onChange={(e) => {
                setFamilyPhone(e.target.value.replace(/\D/g, ''));
                if (familyPhoneError) setFamilyPhoneError(null);
              }}
              disabled={familyLoading}
            />
            {familyPhoneError && <p className="text-xs text-red-600">{familyPhoneError}</p>}
          </div>
          <Button
            onClick={handleSendFamily}
            disabled={familyLoading || familyPhone.length !== 11}
            className="w-full"
          >
            {familyLoading ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                生成中...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                生成家属邀请
              </>
            )}
          </Button>
        </>
      )}

      {familyInvite && (
        <div className="space-y-4">
          <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-800 flex items-start gap-2">
            <MessageCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              {familySmsSent
                ? `家属邀请短信已发送至 ${familyPhone}`
                : '家属邀请已生成。短信通道暂未开通，请把下方链接或二维码转给家属。'}
            </div>
          </div>

          <div className="flex flex-col items-center gap-3">
            <QRCodeDisplay
              value={familyInvite.inviteUrl}
              size={172}
              ariaLabel="家属邀请二维码"
            />
          </div>

          <div className="bg-gray-50 rounded-xl p-3 flex flex-col items-center gap-1">
            <span className="text-xs text-gray-500">家属邀请码</span>
            <span className="text-2xl font-bold tracking-widest text-gray-800 font-mono select-all">
              {formatShortCode(familyInvite.shortCode)}
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => handleCopy(familyInvite.inviteUrl, '家属邀请链接')}
            >
              <Copy className="w-4 h-4" />
              复制链接
            </Button>
            <Button
              variant="ghost"
              className="flex-1"
              onClick={() => {
                setFamilyInvite(null);
                setFamilyPhone('');
                setFamilySmsSent(false);
              }}
            >
              重新生成
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  // 仅在 onboard 成功（'claim' 阶段）后允许切到 family tab
  const showTabs = familyInviteEnabled && onboardResult;
  const effectiveStep: Step = showTabs && step === 'family' ? 'family' : (
    onboardResult ? 'claim' : step
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {onboardResult ? '已开通 CareBot AI 陪伴' : '开通 CareBot AI 陪伴'}
          </DialogTitle>
          <DialogDescription>
            {onboardResult
              ? `${elderName ? `${elderName}的` : '该老人的'} ICOPE 评估已同步到 CareBot。`
              : `请为 ${elderName || '该老人'} 完成 CareBot 开通`}
            {isMock && (
              <span className="ml-1 text-xs text-amber-600">[当前为 mock 模式]</span>
            )}
          </DialogDescription>
        </DialogHeader>

        {showTabs && (
          <div className="flex border-b border-gray-200 -mt-2">
            <button
              type="button"
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                effectiveStep === 'claim'
                  ? 'border-b-2 border-primary-500 text-primary-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setStep('claim')}
            >
              老人扫码认领
            </button>
            <button
              type="button"
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                effectiveStep === 'family'
                  ? 'border-b-2 border-primary-500 text-primary-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setStep('family')}
            >
              老人不会操作？发给家属
            </button>
          </div>
        )}

        {effectiveStep === 'phone' && renderPhoneStep()}
        {effectiveStep === 'sms' && renderSmsStep()}
        {effectiveStep === 'claim' && renderClaimStep()}
        {effectiveStep === 'family' && renderFamilyStep()}
      </DialogContent>
    </Dialog>
  );
};

export default CareBotActivationModal;
